/**
 * CollectionValueDashboard - Comprehensive collection valuation display
 *
 * MDM-UNIFIED-001 Phase 6: Collection Value Dashboard
 *
 * Shows:
 * - Total collection value (raw + graded)
 * - Value breakdown by publisher, era, grading company
 * - Top valued items
 * - Signed vs unsigned value comparison
 *
 * VAULT-S2b OP-2 RELEASED (operator decision): the per-item value is DERIVED at read time
 * from the daily PriceCharting catalog substrate through the ONE ladder in
 * app/services/vault_pricing.py, and GET /vault/valuation reports the SUM of it. This is
 * now MOUNTED twice:
 *   - `compact` on VaultHome (mobile), in place of the old 'Purchase total' placeholder card,
 *     labelled 'Estimated value';
 *   - full on the desktop VaultPage, as the 'Value' panel the header stats badge opens.
 * It fetches through fetchAPI like everything else; the numbers are the server's.
 *
 * @version 1.2.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, Award, Layers, Clock, PieChart, Loader2, AlertCircle, Star, Shield } from 'lucide-react';
import { fetchAPI } from '../../services/api';

// Format currency
const formatMoney = (value) => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format percentage
const formatPercent = (value) => {
  if (!value && value !== 0) return '0%';
  return `${Math.round(value)}%`;
};

// Era colors
const ERA_COLORS = {
  'Golden Age': 'bg-amber-500/20 text-amber-400',
  'Silver Age': 'bg-zinc-400/20 text-zinc-300',
  'Bronze Age': 'bg-orange-600/20 text-orange-400',
  'Copper Age': 'bg-orange-400/20 text-orange-300',
  'Dark Age': 'bg-purple-500/20 text-purple-400',
  'Modern Age': 'bg-blue-500/20 text-blue-400',
  'Unknown': 'bg-zinc-500/20 text-zinc-400',
};

// D45 -- THE HEADLINE MUST NOT CLAIM A MARKET VALUATION IT DOES NOT HAVE.
//
// The per-item ladder (vault_pricing.py) is honest and carries a value_source, but the
// AGGREGATE threw that away: a total built entirely from `purchase` (what the collector paid)
// or `none` (no price at all) rendered identically to a market-backed one, under the words
// "Estimated value".
//
// On PROD 2026-09-17 that was the normal case, not an edge: comic_issues holds 2,593,525 rows
// and SIX populated prices, so catalog tiers resolve for essentially nothing and every item
// falls through to purchase-or-zero. The number was real arithmetic over an empty substrate,
// presented as a valuation.
//
// `priced_items` counts only market_cache / catalog_slabbed / catalog_raw. purchase and none
// are deliberately excluded -- a purchase price is a real number but it is not a valuation.
function describeProvenance(valuation) {
  const priced = valuation?.priced_items ?? 0;
  const total = valuation?.total_items ?? 0;
  const sources = valuation?.value_sources ?? {};
  const marketBacked = priced > 0;

  if (marketBacked) {
    return {
      marketBacked: true,
      headlineLabel: priced < total ? `Estimated value (${priced} of ${total} priced)` : 'Estimated value',
      provenanceNote: null,
    };
  }
  const fromPurchase = sources.purchase ?? 0;
  return {
    marketBacked: false,
    headlineLabel: fromPurchase > 0 ? 'Total paid (no market pricing)' : 'No pricing available',
    provenanceNote:
      fromPurchase > 0
        ? 'Market pricing is unavailable for these items, so this is the sum of what you paid -- not a current valuation.'
        : 'No market price and no purchase price is recorded for these items, so there is nothing to value yet.',
  };
}

export default function CollectionValueDashboard({ compact = false }) {
  const [state, setState] = useState('loading'); // loading, loaded, error
  const [valuation, setValuation] = useState(null);
  const [error, setError] = useState(null);

  const loadValuation = useCallback(async () => {
    setState('loading');
    setError(null);

    try {
      // fetchAPI: cookies + stored bearer + 401 refresh, and the server's error detail
      // (a raw fetch here had none of that and said "Failed to load valuation" for every
      // status).
      const data = await fetchAPI('/vault/valuation');
      setValuation(data);
      setState('loaded');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  }, []);

  useEffect(() => {
    loadValuation();
  }, [loadValuation]);

  // Loading state
  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center py-12" data-testid="collection-value-loading">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  // Error state
  if (state === 'error') {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-500/10 rounded-lg border border-red-500/30" data-testid="collection-value-error">
        <AlertCircle className="w-6 h-6 text-red-400" />
        <div>
          <p className="text-red-400 font-medium">Failed to load valuation</p>
          <p className="text-xs text-red-400/70">{error}</p>
        </div>
      </div>
    );
  }

  if (!valuation) {
    return null;
  }

  // Compact summary card -- the VaultHome mount. 'Estimated value' says what the number is:
  // the server-derived ladder total, not a purchase sum and not a user-entered figure.
  const { marketBacked, headlineLabel, provenanceNote } = describeProvenance(valuation);

  if (compact) {
    return (
      <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-xl border border-zinc-700 p-4" data-testid="collection-value-compact">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className={`w-5 h-5 ${marketBacked ? 'text-green-400' : 'text-zinc-500'}`} />
            <span className="text-sm font-medium text-zinc-300">{headlineLabel}</span>
          </div>
          <span className="text-xs text-zinc-500">{valuation.total_items} items</span>
        </div>
        <p className="text-3xl font-bold text-white mb-2" data-testid="collection-value-total">
          {formatMoney(valuation.total_value_combined)}
        </p>
        {!marketBacked && (
          <p className="text-xs text-amber-400/90 mb-2" data-testid="collection-value-provenance">
            {provenanceNote}
          </p>
        )}
        <div className="flex items-center gap-4 text-sm">
          <span className="text-zinc-400">
            Raw: {formatMoney(valuation.total_value_raw)}
          </span>
          <span className="text-zinc-400">
            Graded: {formatMoney(valuation.total_value_graded)}
          </span>
        </div>
      </div>
    );
  }

  // Full dashboard -- the VaultPage 'Value' panel mount.
  //
  // ⛔ THE PROVENANCE BELONGS HERE MOST OF ALL. The compact card got the honest treatment first
  // and this path did not, which is backwards: this is the surface the 'Value' panel opens, it
  // is four times the type size, and it said "Collection Valuation" over "Total Value" with no
  // qualification whatsoever. A collector reading a purchase-sum under those two headings has
  // been told, twice, that the number is a market valuation.
  //
  // Same describeProvenance() the compact path uses -- not a second copy of the rule, because a
  // second copy is how the two surfaces would come to disagree about the same collection.
  return (
    <div className="space-y-6" data-testid="collection-value-dashboard">
      {/* Hero Stats */}
      <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-xl border border-zinc-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <DollarSign className={`w-5 h-5 ${marketBacked ? 'text-green-400' : 'text-zinc-500'}`} />
            {marketBacked ? 'Collection Valuation' : 'Collection Totals'}
          </h2>
          <span className="text-xs text-zinc-500">
            As of {new Date(valuation.valuation_date).toLocaleDateString()}
          </span>
        </div>

        {!marketBacked && (
          <p
            className="text-xs text-amber-400/90 mb-4"
            data-testid="collection-value-dashboard-provenance"
          >
            {provenanceNote}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Value */}
          <div>
            <p className="text-sm text-zinc-400 mb-1" data-testid="collection-value-dashboard-headline">
              {headlineLabel}
            </p>
            <p className="text-4xl font-bold text-white">
              {formatMoney(valuation.total_value_combined)}
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              {valuation.total_items} items
            </p>
          </div>

          {/* Raw vs Graded */}
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-zinc-400 flex items-center gap-1">
                  <Layers className="w-4 h-4" />
                  Raw Comics
                </span>
                <span className="text-white">{valuation.raw_count}</span>
              </div>
              <p className="text-xl font-semibold text-white">
                {formatMoney(valuation.total_value_raw)}
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-zinc-400 flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  Graded Comics
                </span>
                <span className="text-white">{valuation.graded_count}</span>
              </div>
              <p className="text-xl font-semibold text-white">
                {formatMoney(valuation.total_value_graded)}
              </p>
            </div>
          </div>

          {/* Averages */}
          <div className="space-y-3">
            <div>
              <p className="text-sm text-zinc-400 mb-1">Avg Value Per Item</p>
              <p className="text-xl font-semibold text-white">
                {formatMoney(valuation.avg_value_per_item)}
              </p>
            </div>
            <div className="flex gap-4">
              <div>
                <p className="text-xs text-zinc-500">Avg Raw</p>
                <p className="text-sm font-medium text-white">
                  {formatMoney(valuation.avg_value_raw)}
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Avg Graded</p>
                <p className="text-sm font-medium text-white">
                  {formatMoney(valuation.avg_value_graded)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdowns Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Publisher */}
        {valuation.by_publisher?.length > 0 && (
          <div className="bg-zinc-800/50 rounded-lg border border-zinc-700 overflow-hidden">
            <div className="p-3 border-b border-zinc-700 flex items-center gap-2">
              <PieChart className="w-4 h-4 text-orange-500" />
              <span className="font-medium text-white">By Publisher</span>
            </div>
            <div className="divide-y divide-zinc-700">
              {valuation.by_publisher.map((item) => (
                <div key={item.category} className="p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {item.category}
                    </p>
                    <p className="text-xs text-zinc-500">{item.count} items</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">
                      {formatMoney(item.value)}
                    </p>
                    <p className="text-xs text-zinc-500">{formatPercent(item.percentage)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* By Era */}
        {valuation.by_era?.length > 0 && (
          <div className="bg-zinc-800/50 rounded-lg border border-zinc-700 overflow-hidden">
            <div className="p-3 border-b border-zinc-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="font-medium text-white">By Era</span>
            </div>
            <div className="p-3 flex flex-wrap gap-2">
              {valuation.by_era.map((item) => (
                <div
                  key={item.category}
                  className={`px-3 py-2 rounded-lg ${ERA_COLORS[item.category] || ERA_COLORS['Unknown']}`}
                >
                  <p className="text-sm font-medium">{item.category}</p>
                  <p className="text-xs opacity-75">
                    {item.count} items | {formatMoney(item.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* By Grading Company */}
        {valuation.by_grade_company?.length > 0 && (
          <div className="bg-zinc-800/50 rounded-lg border border-zinc-700 overflow-hidden">
            <div className="p-3 border-b border-zinc-700 flex items-center gap-2">
              <Award className="w-4 h-4 text-orange-500" />
              <span className="font-medium text-white">By Grading Company</span>
            </div>
            <div className="p-3 flex gap-4">
              {valuation.by_grade_company.map((item) => (
                <div
                  key={item.category}
                  className={`flex-1 p-3 rounded-lg ${
                    item.category === 'CGC' ? 'bg-blue-500/20' :
                    item.category === 'CBCS' ? 'bg-yellow-500/20' :
                    'bg-zinc-700/50'
                  }`}
                >
                  <p className={`text-lg font-bold ${
                    item.category === 'CGC' ? 'text-blue-400' :
                    item.category === 'CBCS' ? 'text-yellow-400' :
                    'text-zinc-300'
                  }`}>
                    {item.category}
                  </p>
                  <p className="text-sm text-zinc-300">{item.count} slabs</p>
                  <p className="text-sm font-medium text-white mt-1">
                    {formatMoney(item.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Valued Items */}
        {valuation.top_valued_items?.length > 0 && (
          <div className="bg-zinc-800/50 rounded-lg border border-zinc-700 overflow-hidden">
            <div className="p-3 border-b border-zinc-700 flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="font-medium text-white">Top Valued Items</span>
            </div>
            <div className="divide-y divide-zinc-700">
              {valuation.top_valued_items.slice(0, 5).map((item, idx) => (
                <div key={item.collection_item_id} className="p-3 flex items-center gap-3">
                  <span className="text-lg font-bold text-zinc-500 w-6">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {item.title}
                      {item.issue_number && ` #${item.issue_number}`}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {item.is_graded ? (
                        <span className="text-blue-400">
                          {item.grading_company?.toUpperCase()} {item.grade}
                        </span>
                      ) : (
                        <span>Raw{item.grade && ` - ${item.grade}`}</span>
                      )}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-green-400">
                    {formatMoney(item.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Signed vs Unsigned */}
      {(valuation.signed_count > 0 || valuation.unsigned_count > 0) && (
        <div className="bg-zinc-800/50 rounded-lg border border-zinc-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-orange-500" />
            <span className="font-medium text-white">Signature Impact</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-yellow-500/10 rounded-lg">
              <p className="text-sm text-zinc-400 mb-1">Signed Comics</p>
              <p className="text-xl font-semibold text-yellow-400">
                {formatMoney(valuation.signed_value)}
              </p>
              <p className="text-xs text-zinc-500">{valuation.signed_count} items</p>
            </div>
            <div className="p-3 bg-zinc-700/50 rounded-lg">
              <p className="text-sm text-zinc-400 mb-1">Unsigned Comics</p>
              <p className="text-xl font-semibold text-white">
                {formatMoney(valuation.unsigned_value)}
              </p>
              <p className="text-xs text-zinc-500">{valuation.unsigned_count} items</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
